import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import b from 'b_';
import { Spin, useIntl } from 'platform-components';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { QuizEditorHeader } from './__header/quiz-editor__header';
import { QuizEditorQuestion } from './__question/quiz-editor__question';
import { QuizEditorSlide } from './__slide/quiz-editor__slide';
import { QuizEditorSlidesReel } from './__slides-reel/quiz-editor__slides-reel';
import { actions } from './actions';
import { getQuizLoadingStatus, getQuizToPreview } from './selectors';
import { QuizEditorView } from './typings';
import { useQuizDraftInLocalStorage } from './useQuizDraftInLocalStorage';

import './quiz-editor.scss';

interface Props {
    view?: QuizEditorView;
    readonly?: boolean;
}

interface ParamType {
    uuid?: string;
    lessonId?: string;
}

export const QuizEditor = (props: Props) => {
    const { view = 'create', readonly = false } = props;

    const intl = useIntl();
    const dispatch = useDispatch();
    const { uuid, lessonId } = useParams<ParamType>();
    const { getQuizDraftFromLocalStorage } = useQuizDraftInLocalStorage();
    const [showPreviewAnswers, setShowPreviewAnswers] = useState(false);

    const quizToPreview = useSelector(getQuizToPreview);
    const quizLoadingStatus = useSelector(getQuizLoadingStatus);
    const [shouldShowSpin, setShouldShowSpin] = useState(quizLoadingStatus !== LOADING_STATUSES.SUCCESS);

    useEffect(() => {
        setShouldShowSpin(quizLoadingStatus !== LOADING_STATUSES.SUCCESS);
    }, [quizLoadingStatus]);

    useEffect(() => {
        if (quizToPreview && view !== 'create') {
            dispatch(actions.setQuizDraft(quizToPreview));
        } else if (uuid) {
            dispatch(actions.getQuiz(uuid));
        } else if (lessonId) {
            dispatch(actions.getQuizByLessonId(lessonId));
        } else {
            dispatch(actions.setQuizDraft(getQuizDraftFromLocalStorage()));
        }

        dispatch(actions.setViewType(view));
        dispatch(actions.setIsReadOnly(readonly));
    }, [dispatch, getQuizDraftFromLocalStorage, quizToPreview, readonly, uuid, view]);

    let pageTitleTextId;
    switch (view) {
        case 'create':
            pageTitleTextId = 'teacherLab.quizEditor.pageTitle.create';
            break;
        case 'edit':
            pageTitleTextId = 'teacherLab.quizEditor.pageTitle.edit';
            break;
        case 'preview':
            pageTitleTextId = 'teacherLab.quizEditor.pageTitle.preview';
            break;
    }

    return (
        <>
            <Helmet title={intl.formatMessage({ id: pageTitleTextId })} />
            <div className={b('quiz-editor', { 'with-spin': shouldShowSpin, readonly })}>
                {shouldShowSpin ? (
                    <Spin mix="quiz-editor__spin" />
                ) : (
                    <>
                        <QuizEditorSlidesReel />
                        <QuizEditorSlide
                            body={<QuizEditorQuestion showPreviewAnswers={showPreviewAnswers} />}
                            head={
                                <QuizEditorHeader
                                    onShowPreviewAnswersToggle={setShowPreviewAnswers}
                                    showPreviewAnswers={showPreviewAnswers}
                                />
                            }
                        />
                    </>
                )}
            </div>
        </>
    );
};
